import { GitCommitHorizontal } from 'lucide-react';
import { Link, useLocation } from 'react-router';
import { parseProjectPath, projectPath } from '../modules/platform/index.ts';
import { useCommitView } from '../modules/platform/index.ts';

const ago = (iso: string) => {
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)} min ago`;
  if (s < 86400) return `${Math.floor(s / 3600)} h ago`;
  return new Date(iso).toLocaleDateString();
};

/** On every project page at a commit: which commit, read-only, and the way back to its branch (same page). */
export function CommitBanner({ slug }: { slug: string }) {
  const { info, atCommit } = useCommitView();
  const location = useLocation();
  if (!atCommit || !info) return null;
  const rest = parseProjectPath(location.pathname).rest;
  const c = info.commit;
  return (
    <div
      role="status"
      data-testid="commit-banner"
      className="shrink-0 flex flex-wrap items-center gap-x-2 gap-y-1 px-4 py-2 text-sm border-b border-[var(--pill-amber-bg)] bg-[var(--pill-amber-bg)] text-[var(--pill-amber-dark)]"
    >
      <GitCommitHorizontal size={16} className="shrink-0" />
      <span>
        Viewing commit <code className="font-mono font-semibold">{info.short}</code>
      </span>
      {c && (
        <>
          <span aria-hidden>·</span>
          <span className="truncate max-w-[40ch]" title={c.message} data-testid="commit-banner-message">
            {c.message.split('\n')[0]}
          </span>
          <span aria-hidden>·</span>
          <span title={new Date(c.date).toLocaleString()}>
            {c.author.name}, {ago(c.date)}
          </span>
        </>
      )}
      <span aria-hidden>—</span>
      <span className="font-semibold">read-only</span>
      <span aria-hidden>·</span>
      <Link
        to={projectPath(rest, slug, info.branch) + location.search}
        data-testid="commit-banner-back"
        className="font-semibold underline text-inherit"
      >
        Back to {info.branch}
      </Link>
      <span
        className="ml-auto text-xs opacity-80"
        title="Runs, instances and model versions are keyed by branch"
      >
        Runs and other runtime state shown are {info.branch}&apos;s
      </span>
    </div>
  );
}
